import type { TalentListView } from "@cv-screener/shared";

export type TalentListMemberView = TalentListView["members"][number];
export type TalentMemberSortKey = "candidate" | "source" | "assessment" | "status" | "specialty" | "level" | "note";
export type TalentMemberSortDirection = "ascending" | "descending";

export type TalentMemberFilters = {
  search: string;
  statuses: string[];
  jobIds: string[];
  specialties: string[];
  levels: string[];
};

export type TalentMemberSort = {
  key: TalentMemberSortKey;
  direction: TalentMemberSortDirection;
};

export function defaultTalentMemberSortDirection(key: TalentMemberSortKey): TalentMemberSortDirection {
  return key === "assessment" ? "descending" : "ascending";
}

export function updateTalentMemberSorts(current: TalentMemberSort[], key: TalentMemberSortKey, makePrimary: boolean): TalentMemberSort[] {
  if (makePrimary) return [{ key, direction: defaultTalentMemberSortDirection(key) }];
  const existingIndex = current.findIndex((sort) => sort.key === key);
  if (existingIndex < 0) return [...current, { key, direction: defaultTalentMemberSortDirection(key) }];
  const existing = current[existingIndex]!;
  if (existing.direction !== defaultTalentMemberSortDirection(key)) return current.filter((_, index) => index !== existingIndex);
  return current.map((sort, index) => index === existingIndex
    ? { ...sort, direction: sort.direction === "descending" ? "ascending" : "descending" }
    : sort);
}

function searchableText(member: TalentListMemberView): string {
  const candidatePhone = member.application.candidatePhone;
  return [
    member.application.candidateName,
    member.application.candidateEmail,
    candidatePhone,
    candidatePhone.replace(/\D/g, ""),
    member.application.latestRole,
    member.application.jobId,
    member.application.status,
    member.application.confidence,
    member.application.overallScore,
    member.note,
    ...member.specialties,
    ...member.levels,
  ].join(" ").toLocaleLowerCase();
}

function sortValue(member: TalentListMemberView, key: TalentMemberSortKey): string | number {
  if (key === "candidate") return member.application.candidateName.toLocaleLowerCase();
  if (key === "source") return member.application.jobId.toLocaleLowerCase();
  if (key === "assessment") return member.application.overallScore;
  if (key === "status") return member.application.status.toLocaleLowerCase();
  if (key === "specialty") return member.specialties.join(" ").toLocaleLowerCase();
  if (key === "level") return member.levels.join(" ").toLocaleLowerCase();
  return member.note.toLocaleLowerCase();
}

export function filterAndSortTalentMembers(members: TalentListMemberView[], filters: TalentMemberFilters, sorts: TalentMemberSort[]): TalentListMemberView[] {
  const search = filters.search.trim().toLocaleLowerCase();
  const compactPhoneSearch = /^[+\d\s().-]+$/.test(search) ? search.replace(/\D/g, "") : "";
  return members.filter((member) => {
    const searchable = searchableText(member);
    if (search && !searchable.includes(search) && !(compactPhoneSearch.length >= 4 && searchable.includes(compactPhoneSearch))) return false;
    if (!filters.statuses.includes("all") && !filters.statuses.includes(member.application.status)) return false;
    if (!filters.jobIds.includes("all") && !filters.jobIds.includes(member.application.jobId)) return false;
    if (!filters.specialties.includes("all") && !member.specialties.some((value) => filters.specialties.includes(value))) return false;
    if (!filters.levels.includes("all") && !member.levels.some((value) => filters.levels.includes(value))) return false;
    return true;
  }).sort((first, second) => {
    for (const sort of sorts) {
      const left = sortValue(first, sort.key);
      const right = sortValue(second, sort.key);
      const comparison = typeof left === "number" && typeof right === "number" ? left - right : String(left).localeCompare(String(right));
      if (comparison !== 0) return sort.direction === "descending" ? -comparison : comparison;
    }
    return first.application.candidateName.localeCompare(second.application.candidateName);
  });
}

export function talentMemberFilterOptions(members: TalentListMemberView[]): { statuses: string[]; jobIds: string[]; specialties: string[]; levels: string[] } {
  return {
    statuses: [...new Set(members.map((member) => member.application.status))].sort((left, right) => left.localeCompare(right)),
    jobIds: [...new Set(members.map((member) => member.application.jobId))].sort((left, right) => left.localeCompare(right)),
    specialties: [...new Set(members.flatMap((member) => member.specialties))].sort((left, right) => left.localeCompare(right)),
    levels: [...new Set(members.flatMap((member) => member.levels))].sort((left, right) => left.localeCompare(right)),
  };
}
